import { STORAGE } from "@constants/Storage";
/**
 * Normalizes fetched organizations to exactly the shape `isStoredMembership` accepts, so an entry
 * written here always survives its own round-trip back out of sessionStorage. Entries without a
 * usable `_id` are dropped rather than coerced: the id is what the resolver authorizes the
 * `/o/:orgId` segment against, so an entry lacking one is unusable, while a missing display `name`
 * is merely cosmetic and can safely fall back to an empty string.
 */
export const toStoredMemberships = (organizations) => organizations
    .filter((org) => typeof (org === null || org === void 0 ? void 0 : org._id) === "string" && org._id.length > 0)
    .map((org) => ({
    _id: org._id,
    name: typeof org.name === "string" ? org.name : "",
    role: typeof org.role === "string" ? org.role : null,
}));
/**
 * Shape guard for one mirror entry. `_id` is the value the resolver authorizes the `/o/:orgId`
 * segment against, so it must be a non-empty string or the entry is worthless; `name` and `role`
 * are display-only but are validated too so a partially-written entry can't reach the UI.
 */
const isStoredMembership = (value) => {
    if (typeof value !== "object" || value === null)
        return false;
    const { _id, name, role } = value;
    return (typeof _id === "string" && _id.length > 0 && typeof name === "string" && (role === null || typeof role === "string"));
};
/**
 * Reads the per-tab memberships mirror. Anything can sit under this key — a stale shape from an
 * older build, a hand-edited devtools value, a half-written entry — and it feeds the resolver's
 * authorization check, so a payload that isn't an array of well-formed memberships is treated as
 * "nothing stored" (null) rather than trusted. Null is the same signal a never-populated mirror
 * gives, so ActiveOrgLayout re-fetches from the server and re-authorizes; a malformed mirror can
 * therefore never widen access, only cost one fetch.
 */
export const getStoredMemberships = () => {
    try {
        const membershipsString = sessionStorage.getItem(STORAGE.DISCOVER_MEMBERSHIPS);
        if (!membershipsString)
            return null;
        const parsed = JSON.parse(membershipsString);
        if (!Array.isArray(parsed) || !parsed.every(isStoredMembership))
            return null;
        return parsed;
    }
    catch (_a) {
        console.log("Session Storage access issue!!!");
        return null;
    }
};
/**
 * Picks the organization a page should act on, given an optionally requested one (`?org=`) and the
 * organizations the caller is actually authorized for.
 *
 * `?org=` is a UI convenience — the header organization picker writes it so a choice survives a
 * reload — not an authorization claim. `ActiveOrgLayout` authorizes the `/o/:orgId` path segment
 * against the memberships the caller holds; letting a hand-edited query param outrank that decision
 * would reopen the same hole from the other side, pointing billing, user-management and mutation
 * calls at an arbitrary organization id. So the param is honoured only when it names one of
 * `authorizedOrgIds`, and the already-authorized `fallbackOrgId` wins otherwise.
 *
 * `authorizedOrgIds: null` means the authorized set is not known yet (e.g. the discover-org
 * catalogue a staff surface depends on hasn't resolved). Nothing can be cleared against an unknown
 * set, so the param is ignored for that window rather than optimistically trusted.
 *
 * Returns `""` when nothing is authorized — a staff surface has no resolved active organization to
 * fall back to, and no organization at all is the correct answer there, not the requested one.
 */
export const resolveAuthorizedOrgId = ({ requestedOrgId, authorizedOrgIds, fallbackOrgId, }) => {
    if (requestedOrgId && (authorizedOrgIds === null || authorizedOrgIds === void 0 ? void 0 : authorizedOrgIds.includes(requestedOrgId)))
        return requestedOrgId;
    return fallbackOrgId !== null && fallbackOrgId !== void 0 ? fallbackOrgId : "";
};
export const storeMemberships = (memberships) => {
    try {
        sessionStorage.setItem(STORAGE.DISCOVER_MEMBERSHIPS, JSON.stringify(memberships));
    }
    catch (_a) {
        console.log("Session Storage access issue!!!");
    }
};
/**
 * Drops the mirror so the next reader re-fetches instead of trusting it. Removing the key (rather
 * than storing `[]`) is what makes the re-fetch happen: `[]` is a confirmed zero, `null` is "we
 * don't know", and only the latter triggers a fetch.
 */
export const clearStoredMemberships = () => {
    try {
        sessionStorage.removeItem(STORAGE.DISCOVER_MEMBERSHIPS);
    }
    catch (_a) {
        console.log("Session Storage access issue!!!");
    }
};
